import api from '../services/api';

/**
 * Practice branding for print letterheads and patient-facing output.
 *
 * GET /api/settings/branding derives name / subtitle / contact strip from
 * the tenant + locations rows and returns the uploaded logo (a data URI in
 * tenants.settings.branding.logo_data) or the bundled DTC asset for the
 * dt-chiropractic tenant. The legacy constants below exist only for the
 * offline/unfetchable case so DTC print paths never regress to a blank
 * header.
 */
const LEGACY_DTC_BRAND = {
  name: 'DT Chiropractic',
  slug: 'dt-chiropractic',
  subtitle: 'Canton  ·  Cartersville  ·  Rome, Georgia',
  contact_strip: 'Canton (770) 580-0123  ·  Cartersville (470) 888-4888  ·  Rome (706) 388-3888  ·  dtchiropractic.com',
  website: 'dtchiropractic.com',
  logo_src: '/brand/dt-chiropractic-logo.png',
  has_custom_logo: false,
};

let cached = null;

export async function loadPracticeBrand() {
  if (cached) return cached;
  try {
    const { data } = await api.get('/settings/branding');
    cached = data;
    try { localStorage.setItem('practiceBrand', JSON.stringify(data)); } catch { /* storage full/blocked */ }
  } catch {
    try {
      const stored = localStorage.getItem('practiceBrand');
      cached = stored ? JSON.parse(stored) : LEGACY_DTC_BRAND;
    } catch {
      cached = LEGACY_DTC_BRAND;
    }
  }
  return cached;
}

/**
 * Synchronous read for template builders that can't await (warmed by the
 * AppShell load effect / any prior loadPracticeBrand call). Null when
 * nothing is cached yet — callers keep their legacy fallback.
 */
export function getCachedPracticeBrand() {
  if (cached) return cached;
  try {
    const stored = localStorage.getItem('practiceBrand');
    if (stored) { cached = JSON.parse(stored); return cached; }
  } catch { /* fall through */ }
  return null;
}

/** Call after the practice name, locations, or logo change in Settings. */
export function clearPracticeBrandCache() {
  cached = null;
  try { localStorage.removeItem('practiceBrand'); } catch { /* noop */ }
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The letterhead block every client print path shares (and the server PDF
 * letterhead mirrors). `titleText` is the uppercase form/document tag in
 * the brand band. `logoOrigin` prefixes relative logo paths for print
 * windows opened on a different document origin.
 */
export function brandLetterheadHtml(brand, titleText, { logoOrigin = '' } = {}) {
  const b = brand || LEGACY_DTC_BRAND;
  const logoSrc = b.logo_src
    ? (b.logo_src.startsWith('data:') ? b.logo_src : `${logoOrigin}${b.logo_src}`)
    : null;
  const mark = logoSrc
    ? `<img class="brand-logo" src="${esc(logoSrc)}" alt="${esc(b.name)}" />`
    : `<div style="font-size:20px;font-weight:800;color:#1A1A1A;letter-spacing:-0.3px;">${esc(b.name)}</div>`;
  return `<div class="letterhead">
    <div class="brand-band">
      ${mark}
      <div class="brand-text">
        <div class="brand-form">${esc(titleText || '')}</div>
        ${b.subtitle ? `<div class="brand-subtitle">${esc(b.subtitle)}</div>` : ''}
      </div>
    </div>
    ${b.contact_strip ? `<div class="contact-strip">${esc(b.contact_strip)}</div>` : ''}
  </div>`;
}
